<script>
  import { apiFetch } from '../lib/auth.js';

  // ── Records ────────────────────────────────────────────────────────
  export let records = [];

  // ── Expand / edit state ────────────────────────────────────────────
  let expandedUserId  = null;
  let userSaveLoad    = {};
  let userSaveError   = {};
  let userSaveSuccess = {};
  let userEdits       = {};

  const ALL_STATUSES = ['pending', 'active', 'banned', 'deactivated'];
  const ALL_ROLES    = ['admin', 'moderator'];

  const STATUS_COLOR = {
    active:      '#6db800',
    pending:     '#cc8833',
    banned:      '#cc3300',
    deactivated: '#444',
  };

  function toggleUserExpand(u) {
    if (expandedUserId === u.id) { expandedUserId = null; return; }
    expandedUserId = u.id;
    if (!userEdits[u.id]) {
      userEdits[u.id] = {
        status: u.status || 'active',
        roles:  Array.isArray(u.roles) ? [...u.roles] : [],
      };
    }
  }

  function toggleRole(userId, role) {
    const edit = userEdits[userId];
    if (!edit) return;
    edit.roles = edit.roles.includes(role)
      ? edit.roles.filter(r => r !== role)
      : [...edit.roles, role];
    userEdits = { ...userEdits };
  }

  async function saveUser(u) {
    const edit = userEdits[u.id];
    if (!edit) return;
    userSaveLoad[u.id]    = true;
    userSaveError[u.id]   = '';
    userSaveSuccess[u.id] = false;
    userSaveLoad = { ...userSaveLoad };
    try {
      const updated = await apiFetch(`/admin/users/${u.id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: edit.status, roles: edit.roles }),
      });
      records = records.map(r => r.id === u.id ? { ...r, ...updated } : r);
      userEdits[u.id] = { status: updated.status, roles: updated.roles || [] };
      userEdits = { ...userEdits };
      userSaveSuccess[u.id] = true;
      setTimeout(() => { userSaveSuccess[u.id] = false; userSaveSuccess = { ...userSaveSuccess }; }, 1500);
    } catch (e) {
      userSaveError[u.id] = e.message || 'Failed to save';
    } finally {
      userSaveLoad[u.id] = false;
      userSaveLoad = { ...userSaveLoad };
    }
  }

  // ── User reviews ───────────────────────────────────────────────────
  let userReviews     = {};
  let userReviewLoad  = {};
  let userReviewError = {};
  let visibilityLoad  = {};

  async function loadUserReviews(userId) {
    if (userReviews[userId]) return;
    userReviewLoad[userId]  = true;
    userReviewError[userId] = '';
    userReviewLoad = { ...userReviewLoad };
    try {
      const data = await apiFetch(`/admin/users/${userId}/reviews`);
      userReviews[userId] = Array.isArray(data) ? data : [];
    } catch (e) {
      userReviewError[userId] = e.message || 'Failed to load reviews';
      userReviews[userId] = [];
    } finally {
      userReviewLoad[userId] = false;
      userReviewLoad = { ...userReviewLoad };
      userReviews = { ...userReviews };
    }
  }

  async function setVisibility(userId, reviewId, isPublic) {
    visibilityLoad[reviewId] = true;
    visibilityLoad = { ...visibilityLoad };
    try {
      await apiFetch(`/admin/reviews/${reviewId}/visibility`, {
        method: 'PUT',
        body: JSON.stringify({ is_public: isPublic }),
      });
      userReviews[userId] = userReviews[userId].map(r =>
        r.id === reviewId ? { ...r, is_public: isPublic } : r
      );
      userReviews = { ...userReviews };
    } catch (e) {
      alert(e.message || 'Failed to update visibility');
    } finally {
      visibilityLoad[reviewId] = false;
      visibilityLoad = { ...visibilityLoad };
    }
  }

  function formatDateTime(str) {
    if (!str) return '—';
    return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }
</script>

<!-- ── List header ──────────────────────────────────────────────────── -->
<div class="list-header">
  <span class="record-count">{records.length} users</span>
  <button class="btn-add-top" on:click={() => { records = []; }}>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
      <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.5"/>
    </svg>
    Refresh
  </button>
</div>

{#if records.length === 0}
  <div class="state-msg empty">No users found.</div>
{:else}
  <div class="list">
    {#each records as u (u.id)}
      <div class="user-block" class:expanded={expandedUserId === u.id}>

        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <div
          class="list-row user-row"
          role="button" tabindex="0"
          on:click={() => toggleUserExpand(u)}
          on:keydown={e => e.key === 'Enter' && toggleUserExpand(u)}
        >
          <div class="user-avatar">
            <span>{(u.username || '?')[0].toUpperCase()}</span>
          </div>
          <div class="user-summary">
            <span class="user-name">{u.username}</span>
            <span class="user-email">{u.email}</span>
          </div>
          <div class="user-meta">
            {#if Array.isArray(u.roles) && u.roles.length > 0}
              {#each u.roles as role}
                <span class="role-badge">{role}</span>
              {/each}
            {/if}
            <span
              class="status-badge"
              style="color: {STATUS_COLOR[u.status] || '#888'}; border-color: {STATUS_COLOR[u.status] || '#333'}"
            >{u.status || 'unknown'}</span>
          </div>
          <svg
            class="row-chevron"
            class:rotated={expandedUserId === u.id}
            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
          >
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>

        {#if expandedUserId === u.id}
          <div class="user-detail">

            <div class="user-detail-grid">
              <div class="user-info-section">
                <div class="section-label">Profile</div>
                {#if u.bio}
                  <p class="user-bio">{u.bio}</p>
                {:else}
                  <p class="user-bio empty">No bio.</p>
                {/if}
                <div class="user-info-row">
                  <span class="info-key">Joined</span>
                  <span class="info-val">{formatDateTime(u.created_at)}</span>
                </div>
                {#if u.country}
                  <div class="user-info-row">
                    <span class="info-key">Country</span>
                    <span class="info-val">{u.country}</span>
                  </div>
                {/if}
                {#if u.timezone}
                  <div class="user-info-row">
                    <span class="info-key">Timezone</span>
                    <span class="info-val">{u.timezone}</span>
                  </div>
                {/if}
              </div>

              <div class="user-info-section">
                <div class="section-label">Contributions</div>
                {#if u.total_reviews != null}
                  <div class="user-info-row">
                    <span class="info-key">Reviews</span>
                    <span class="info-val highlight">{u.total_reviews}</span>
                  </div>
                {/if}
                {#if u.unique_bands_reviewed != null}
                  <div class="user-info-row">
                    <span class="info-key">Unique bands</span>
                    <span class="info-val">{u.unique_bands_reviewed}</span>
                  </div>
                {/if}
                {#if u.average_rating != null}
                  <div class="user-info-row">
                    <span class="info-key">Avg rating</span>
                    <span class="info-val">{Number(u.average_rating).toFixed(1)}</span>
                  </div>
                {/if}
                {#if u.total_reviews == null}
                  <p class="user-bio empty">No contribution data.</p>
                {/if}
              </div>
            </div>

            <!-- Reviews sub-panel -->
            <div class="admin-controls">
              <div class="reviews-header">
                <div class="section-label">Reviews</div>
                {#if !userReviews[u.id]}
                  <button
                    class="btn-load-reviews"
                    disabled={userReviewLoad[u.id]}
                    on:click={() => loadUserReviews(u.id)}
                  >
                    {userReviewLoad[u.id] ? 'Loading...' : 'Load Reviews'}
                  </button>
                {/if}
              </div>

              {#if userReviewError[u.id]}
                <div class="form-error">{userReviewError[u.id]}</div>
              {/if}

              {#if userReviews[u.id]}
                {#if userReviews[u.id].length === 0}
                  <p class="user-bio empty">No reviews yet.</p>
                {:else}
                  <div class="review-list">
                    {#each userReviews[u.id] as review (review.id)}
                      <div class="review-row" class:depublished={!review.is_public}>
                        <div class="review-meta">
                          <span class="review-artist">{review.artist_name || '—'}</span>
                          <span class="review-event">{review.event_name || '—'}</span>
                          {#if review.performance_date}
                            <span class="review-date">{review.performance_date}</span>
                          {/if}
                        </div>
                        <div class="review-right">
                          <span class="review-rating">{review.rating}/10</span>
                          {#if review.headline}
                            <span class="review-headline">{review.headline}</span>
                          {/if}
                          <button
                            class="btn-visibility"
                            class:is-public={review.is_public}
                            disabled={visibilityLoad[review.id]}
                            title={review.is_public ? 'Depublish' : 'Publish'}
                            on:click={() => setVisibility(u.id, review.id, !review.is_public)}
                          >
                            {#if visibilityLoad[review.id]}
                              …
                            {:else if review.is_public}
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                <circle cx="12" cy="12" r="3"/>
                              </svg>
                            {:else}
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                                <line x1="1" y1="1" x2="23" y2="23"/>
                              </svg>
                            {/if}
                          </button>
                        </div>
                      </div>
                    {/each}
                  </div>
                {/if}
              {/if}
            </div>

            <!-- Admin controls -->
            {#if userEdits[u.id]}
              <div class="admin-controls">
                <div class="section-label">Admin Controls</div>

                <div class="controls-row">
                  <div class="control-group">
                    <span class="control-label">Status</span>
                    <div class="status-options">
                      {#each ALL_STATUSES as s}
                        <button
                          class="option-btn"
                          class:selected={userEdits[u.id].status === s}
                          style={userEdits[u.id].status === s ? `border-color: ${STATUS_COLOR[s]}; color: ${STATUS_COLOR[s]}` : ''}
                          on:click={() => { userEdits[u.id].status = s; userEdits = { ...userEdits }; }}
                        >{s}</button>
                      {/each}
                    </div>
                  </div>

                  <div class="control-group">
                    <span class="control-label">Roles</span>
                    <div class="status-options">
                      {#each ALL_ROLES as role}
                        <button
                          class="option-btn"
                          class:selected={userEdits[u.id].roles.includes(role)}
                          on:click={() => toggleRole(u.id, role)}
                        >{role}</button>
                      {/each}
                    </div>
                  </div>
                </div>

                {#if userSaveError[u.id]}
                  <div class="form-error">{userSaveError[u.id]}</div>
                {/if}

                <div class="save-row">
                  {#if userSaveSuccess[u.id]}
                    <span class="save-ok">✓ Saved</span>
                  {/if}
                  <button
                    class="btn-save-user"
                    disabled={userSaveLoad[u.id]}
                    on:click={() => saveUser(u)}
                  >
                    {userSaveLoad[u.id] ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            {/if}

          </div>
        {/if}
      </div>
    {/each}
  </div>
{/if}

<style>
  .user-block { border-bottom: 1px solid #0f1a00; }
  .user-block:last-child { border-bottom: none; }
  .user-block.expanded { background: #050b00; }
  .user-row { border-bottom: none !important; }

  .user-avatar {
    width: 30px; height: 30px; flex-shrink: 0;
    background: #1a3300; border: 1px solid #2a5200; border-radius: 2px;
    display: flex; align-items: center; justify-content: center;
  }
  .user-avatar span { font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.85rem; color: #4a7c00; line-height: 1; }
  .user-summary { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 0.1rem; }
  .user-name { font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.9rem; color: #c8c8c8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .user-email { font-family: 'Rajdhani', sans-serif; font-size: 0.65rem; color: #2a5200; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .user-meta { display: flex; align-items: center; gap: 0.35rem; flex-wrap: wrap; justify-content: flex-end; }

  .role-badge {
    font-family: 'Rajdhani', sans-serif; font-size: 0.48rem; font-weight: 700;
    letter-spacing: 0.12em; text-transform: uppercase;
    background: rgba(180,120,0,0.15); border: 1px solid rgba(180,120,0,0.4);
    color: #cc8833; border-radius: 2px; padding: 0.1rem 0.35rem;
  }
  .status-badge {
    font-family: 'Rajdhani', sans-serif; font-size: 0.48rem; font-weight: 700;
    letter-spacing: 0.12em; text-transform: uppercase;
    border: 1px solid; border-radius: 2px; padding: 0.1rem 0.35rem;
  }

  .user-detail { padding: 0.75rem 1rem 1rem 1rem; border-top: 1px solid #0f1a00; }
  .user-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1rem; }
  .user-info-section { display: flex; flex-direction: column; gap: 0.3rem; }
  .section-label { font-family: 'Rajdhani', sans-serif; font-size: 0.5rem; font-weight: 700; letter-spacing: 0.25em; text-transform: uppercase; color: #3a6b00; margin-bottom: 0.25rem; }
  .user-bio { font-family: 'Rajdhani', sans-serif; font-size: 0.75rem; color: #888; margin: 0 0 0.4rem 0; line-height: 1.4; }
  .user-bio.empty { color: #2a5200; font-style: italic; }
  .user-info-row { display: flex; align-items: baseline; gap: 0.5rem; }
  .info-key { font-family: 'Rajdhani', sans-serif; font-size: 0.55rem; color: #3a6b00; letter-spacing: 0.1em; text-transform: uppercase; flex-shrink: 0; }
  .info-val { font-family: 'Rajdhani', sans-serif; font-size: 0.75rem; color: #c8c8c8; font-weight: 600; }
  .info-val.highlight { color: #6db800; }

  .admin-controls { border-top: 1px solid #0f1a00; padding-top: 0.75rem; margin-top: 0.75rem; }
  .controls-row { display: flex; gap: 1.5rem; margin-bottom: 0.75rem; flex-wrap: wrap; }
  .control-group { display: flex; flex-direction: column; gap: 0.4rem; }
  .control-label { font-family: 'Rajdhani', sans-serif; font-size: 0.5rem; font-weight: 700; letter-spacing: 0.2em; text-transform: uppercase; color: #3a6b00; }
  .status-options { display: flex; gap: 0.35rem; flex-wrap: wrap; }

  .save-row { display: flex; align-items: center; justify-content: flex-end; gap: 0.75rem; margin-top: 0.5rem; }
  .save-ok { font-family: 'Rajdhani', sans-serif; font-size: 0.6rem; font-weight: 700; color: #6db800; letter-spacing: 0.15em; }
  .btn-save-user {
    background: #1a3300; border: 1px solid #3a6b00; color: #6db800;
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.55rem;
    letter-spacing: 0.2em; text-transform: uppercase; padding: 0.45rem 0.85rem;
    border-radius: 2px; cursor: pointer; transition: background 0.15s;
  }
  .btn-save-user:hover { background: #223d00; }
  .btn-save-user:disabled { opacity: 0.4; cursor: not-allowed; }

  /* Reviews sub-panel */
  .reviews-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem; }
  .btn-load-reviews {
    background: #0d1a00; border: 1px solid #2a5200; color: #4a7c00;
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.5rem;
    letter-spacing: 0.2em; text-transform: uppercase; padding: 0.25rem 0.6rem;
    border-radius: 2px; cursor: pointer; transition: border-color 0.15s, color 0.15s;
  }
  .btn-load-reviews:hover { border-color: #3a6b00; color: #6db800; }
  .btn-load-reviews:disabled { opacity: 0.4; cursor: not-allowed; }

  .review-list { display: flex; flex-direction: column; gap: 0.25rem; }
  .review-row {
    display: flex; align-items: center; justify-content: space-between; gap: 0.75rem;
    padding: 0.4rem 0.6rem; background: #0a0a0a; border: 1px solid #1e3d00; border-radius: 2px;
  }
  .review-row.depublished { border-color: #1a0a00; opacity: 0.6; }
  .review-meta { display: flex; align-items: center; gap: 0.5rem; flex: 1; min-width: 0; flex-wrap: wrap; }
  .review-artist { font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.8rem; color: #c8c8c8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .review-event { font-family: 'Rajdhani', sans-serif; font-size: 0.65rem; color: #3a6b00; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .review-date { font-family: 'Rajdhani', sans-serif; font-size: 0.55rem; color: #2a5200; letter-spacing: 0.08em; }
  .review-right { display: flex; align-items: center; gap: 0.5rem; flex-shrink: 0; }
  .review-rating { font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.75rem; color: #6db800; min-width: 2.5rem; text-align: right; }
  .review-headline { font-family: 'Rajdhani', sans-serif; font-size: 0.65rem; color: #555; max-width: 120px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .btn-visibility {
    background: none; border: 1px solid #1e3d00; border-radius: 2px;
    color: #2a5200; cursor: pointer; padding: 0.2rem 0.3rem;
    display: flex; align-items: center; transition: border-color 0.15s, color 0.15s;
    font-family: 'Rajdhani', sans-serif; font-size: 0.65rem;
  }
  .btn-visibility svg { width: 12px; height: 12px; }
  .btn-visibility.is-public { border-color: #2a5200; color: #6db800; }
  .btn-visibility:hover, .btn-visibility.is-public:hover { border-color: #cc3300; color: #cc3300; }
  .btn-visibility:disabled { opacity: 0.4; cursor: not-allowed; }
</style>
